import React from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Modal, ModalHeader, ModalContent, ModalFooter } from '@/shared/components/ui/Modal';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Select } from '@/shared/components/ui/Select';
import { FormField } from '@/shared/components/ui/FormField';
import { Checkbox } from '@/shared/components/ui/Checkbox';
import { Icon } from '@/shared/icons';
import type { CreditAlert, CreateCreditAlertRequest } from '../types';

const alertSchema = z.object({
  triggerType: z.enum(['balance_below', 'daily_consumption_above']),
  triggerValue: z.number({ invalid_type_error: 'Informe um valor' }).min(1, 'Valor deve ser maior que 0'),
  channelSocket: z.boolean(),
  channelEmail: z.boolean(),
  channelWhatsapp: z.boolean(),
  recipients: z.string(),
  cooldownMinutes: z.number({ invalid_type_error: 'Informe o intervalo' }).min(1, 'Minimo 1 minuto'),
}).refine(
  (data) => data.channelSocket || data.channelEmail || data.channelWhatsapp,
  { message: 'Selecione ao menos um canal', path: ['channelSocket'] }
);

type FormData = z.infer<typeof alertSchema>;

interface CreditAlertFormProps {
  isOpen: boolean;
  onClose: () => void;
  alert?: CreditAlert;
  alertIndex?: number;
  onSubmit: (data: CreateCreditAlertRequest) => void;
  isLoading?: boolean;
}

const TRIGGER_OPTIONS = [
  { value: 'balance_below', label: 'Saldo abaixo de' },
  { value: 'daily_consumption_above', label: 'Consumo diario acima de' },
];

/**
 * Modal de criação/edição de alertas de crédito — Modal shared (F72).
 */
export const CreditAlertForm: React.FC<CreditAlertFormProps> = ({
  isOpen,
  onClose,
  alert,
  onSubmit,
  isLoading,
}) => {
  const isEditing = !!alert;

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(alertSchema),
    defaultValues: {
      triggerType: alert?.trigger.type ?? 'balance_below',
      triggerValue: alert?.trigger.value ?? 100,
      channelSocket: alert?.notification.channels.includes('socket') ?? true,
      channelEmail: alert?.notification.channels.includes('email') ?? false,
      channelWhatsapp: alert?.notification.channels.includes('whatsapp') ?? false,
      recipients: alert?.notification.recipients.join(', ') ?? '',
      cooldownMinutes: alert?.cooldownMinutes ?? 60,
    },
  });

  const handleFormSubmit = (data: FormData) => {
    const channels: ('socket' | 'email' | 'whatsapp')[] = [];
    if (data.channelSocket) channels.push('socket');
    if (data.channelEmail) channels.push('email');
    if (data.channelWhatsapp) channels.push('whatsapp');

    const recipients = data.recipients
      .split(',')
      .map((r) => r.trim())
      .filter(Boolean);

    onSubmit({
      trigger: {
        type: data.triggerType,
        value: data.triggerValue,
      },
      notification: {
        channels,
        recipients,
      },
      cooldownMinutes: data.cooldownMinutes,
    });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} maxWidth="md">
      <div data-testid="credit-alert-form-modal">
        <ModalHeader title={isEditing ? 'Editar Alerta' : 'Novo Alerta'} onClose={onClose}>
          <div
            className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: '#ede9fe' }}
          >
            <Icon name="bell" className="h-5 w-5" style={{ color: '#8b5cf6' }} />
          </div>
        </ModalHeader>

        <form onSubmit={handleSubmit(handleFormSubmit)}>
          <ModalContent className="px-6 py-5 space-y-4" padding={false}>
            <Controller
              name="triggerType"
              control={control}
              render={({ field }) => (
                <FormField label="Tipo de alerta" error={errors.triggerType?.message} required>
                  <Select
                    options={TRIGGER_OPTIONS}
                    value={field.value}
                    onChange={field.onChange}
                    placeholder="Selecione o tipo"
                    data-testid="credit-alert-trigger-type"
                  />
                </FormField>
              )}
            />

            <FormField label="Valor" error={errors.triggerValue?.message} required>
              <Input
                {...register('triggerValue', { valueAsNumber: true })}
                type="number"
                placeholder="Ex: 100"
                min="1"
                data-testid="credit-alert-trigger-value"
              />
            </FormField>

            <div>
              <p className="text-sm font-medium mb-2" style={{ color: '#1e293b' }}>
                Canais de notificacao *
              </p>
              {errors.channelSocket?.message && (
                <p className="text-xs mb-2" style={{ color: '#ef4444' }} data-testid="credit-alert-channels-error">
                  {errors.channelSocket.message}
                </p>
              )}
              <div className="space-y-2">
                <Controller
                  name="channelSocket"
                  control={control}
                  render={({ field }) => (
                    <Checkbox
                      label="Notificacao no sistema"
                      checked={field.value}
                      onChange={field.onChange}
                    />
                  )}
                />
                <Controller
                  name="channelEmail"
                  control={control}
                  render={({ field }) => (
                    <Checkbox
                      label="E-mail"
                      checked={field.value}
                      onChange={field.onChange}
                    />
                  )}
                />
                <Controller
                  name="channelWhatsapp"
                  control={control}
                  render={({ field }) => (
                    <Checkbox
                      label="WhatsApp"
                      checked={field.value}
                      onChange={field.onChange}
                    />
                  )}
                />
              </div>
            </div>

            <FormField
              label="Destinatarios"
              description="E-mails separados por vírgula, usados no canal E-mail. A notificação no sistema e o WhatsApp vão para quem tem acesso ao módulo Empresa."
              error={errors.recipients?.message}
            >
              <Input
                {...register('recipients')}
                placeholder="email@exemplo.com"
                data-testid="credit-alert-recipients"
              />
            </FormField>

            <FormField label="Cooldown (minutos)" error={errors.cooldownMinutes?.message} required>
              <Input
                {...register('cooldownMinutes', { valueAsNumber: true })}
                type="number"
                placeholder="60"
                min="1"
                data-testid="credit-alert-cooldown"
              />
            </FormField>
          </ModalContent>

          <ModalFooter>
            <Button type="button" variant="secondary" onClick={onClose} data-testid="credit-alert-cancel">
              Cancelar
            </Button>
            <Button type="submit" loading={isLoading} data-testid="credit-alert-submit">
              {isEditing ? 'Salvar' : 'Criar Alerta'}
            </Button>
          </ModalFooter>
        </form>
      </div>
    </Modal>
  );
};

CreditAlertForm.displayName = 'CreditAlertForm';
